import {useCallback, useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router';
import {z} from 'zod';

export interface UseSearchParamsHandlerOptions<T extends Record<string, any>> {
  schema: z.ZodSchema<T>;
  defaultValues?: Partial<T>;
}

export interface UseSearchParamsHandlerReturn<T extends Record<string, any>> {
  searchParams: T;
  setSearchParam: <K extends keyof T>(key: K, value: T[K] | undefined) => void;
  setSearchParams: (values: Partial<T>) => void;
  clearSearchParams: () => void;
}

/**
 * Wrapper hook for useSearchParams that stores data as an object with schema validation.
 * The object is stored in search params in the URL.
 * When setSearchParams is called, it updates both the query in URL and the state.
 *
 * @param options - Configuration options including schema and default values
 * @returns Object with searchParams and setter functions
 */
export function useSearchParamsHandler<T extends Record<string, any>>(
  options: UseSearchParamsHandlerOptions<T>,
): UseSearchParamsHandlerReturn<T> {
  const {schema, defaultValues = {}} = options;
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();

  // Parse and validate search params from URL
  const searchParams = useMemo(() => {
    const rawParams: Record<string, any> = {};

    // Extract all params from URL
    urlSearchParams.forEach((value, key) => {
      // Try to parse as JSON for complex types, otherwise use as string
      try {
        rawParams[key] = JSON.parse(value);
      } catch {
        rawParams[key] = value;
      }
    });

    // Merge with default values
    const mergedParams = {...defaultValues, ...rawParams};

    // Validate with schema
    try {
      return schema.parse(mergedParams);
    } catch (error) {
      // If validation fails, return default values
      console.warn('URL search params validation failed, using defaults:', error);
      return schema.parse(defaultValues);
    }
  }, [urlSearchParams, schema, defaultValues]);

  // Update URL with new search params
  const updateUrl = useCallback(
    (newParams: Partial<T>, replace = true) => {
      const currentParams = new URLSearchParams(urlSearchParams);

      // Update params
      Object.entries(newParams).forEach(([key, value]) => {
        const defaultValue = (defaultValues as Record<string, any>)?.[key];

        // Remove param if undefined, null, empty string, or matches default value
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (defaultValue !== undefined && value === defaultValue)
        ) {
          currentParams.delete(key);
        } else {
          // Stringify complex types
          const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
          currentParams.set(key, stringValue);
        }
      });

      // Navigate with updated params - updates both URL and state
      const newSearch = currentParams.toString();
      navigate(
        {
          search: newSearch ? `?${newSearch}` : '',
        },
        {replace},
      );
    },
    [navigate, urlSearchParams, defaultValues],
  );

  // Set a single search param
  const setSearchParam = useCallback(
    <K extends keyof T>(key: K, value: T[K] | undefined) => {
      updateUrl({[key]: value} as Partial<T>);
    },
    [updateUrl],
  );

  // Set multiple search params
  const setSearchParams = useCallback(
    (values: Partial<T>) => {
      updateUrl(values);
    },
    [updateUrl],
  );

  // Clear all search params
  const clearSearchParams = useCallback(() => {
    navigate({search: ''}, {replace: true});
  }, [navigate]);

  return {
    searchParams,
    setSearchParam,
    setSearchParams,
    clearSearchParams,
  };
}
